import React, { useMemo, useState } from 'react';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fmt } from 'pages/VocabularyTest/cefr';
import {
  filterWordsByQuery,
  ResultCategoryBuckets,
} from 'pages/VocabularyTest/resultFormat';
import { formatVocabularyTestWord } from 'pages/VocabularyTest/utils';
import { STATUS_META } from 'components/dictionaryView/statusMeta';
import CompactPagination from 'components/CompactPagination';

type ResultTabKey = 'learned' | 'repeat' | 'active';

interface TestResultWordsTableProps {
  categories: ResultCategoryBuckets;
}

const PAGE_SIZE = 10;

// Tab render order is driven by this object's key order (Object.keys):
// Активне мовлення → Вивчено → Повторити.
const TAB_LABELS: Record<ResultTabKey, string> = {
  active: 'Активне мовлення',
  learned: 'Вивчено',
  repeat: STATUS_META.repeat.label,
};

const TestResultWordsTable: React.FC<TestResultWordsTableProps> = ({
  categories,
}) => {
  const [tab, setTab] = useState<ResultTabKey>('active');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const tabWords = categories[tab];
  const filtered = useMemo(
    () => filterWordsByQuery(tabWords, query),
    [tabWords, query],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageWords = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const changeTab = (next: ResultTabKey) => {
    setTab(next);
    setQuery('');
    setPage(1);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: '22px 24px 0' }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 2.25 }}>
          {'Слова з тесту'}
        </Typography>
        <Stack direction={'row'} flexWrap={'wrap'} gap={1.5} alignItems={'center'}>
          <Stack direction={'row'} flexWrap={'wrap'} gap={1.25}>
            {(Object.keys(TAB_LABELS) as ResultTabKey[]).map((key) => {
              const isActive = key === tab;
              return (
                <Box
                  key={key}
                  component={'button'}
                  onClick={() => changeTab(key)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    height: 38,
                    px: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'primary.tableBorder',
                    bgcolor: isActive ? 'primary.purple' : 'transparent',
                    color: isActive ? 'primary.main' : 'text.primary',
                    fontSize: 15,
                    fontWeight: isActive ? 600 : 400,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {TAB_LABELS[key]}
                  <Box component={'span'} sx={{ opacity: 0.7 }}>
                    {fmt(categories[key].length)}
                  </Box>
                </Box>
              );
            })}
          </Stack>
          <TextField
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder={'Пошук слова або перекладу…'}
            size={'small'}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position={'start'}>
                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '28% 32% 40%',
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
        }}
      >
        {['Слово', 'Транскрипція', 'Переклад'].map((h) => (
          <Box
            key={h}
            sx={{
              bgcolor: 'primary.tableHeadBg',
              fontWeight: 700,
              fontSize: 15,
              p: '10px 16px',
              borderBottom: '1px solid',
              borderColor: 'primary.tableBorder',
            }}
          >
            {h}
          </Box>
        ))}

        {pageWords.length === 0 && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: '44px',
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: 15,
            }}
          >
            {'Нічого не знайдено за цим запитом'}
          </Box>
        )}

        {pageWords.map((word, index) => (
          <React.Fragment key={`${word.wordKind}-${word.word}-${index}`}>
            <Box
              sx={{
                fontSize: 19,
                fontWeight: 600,
                p: '14px 16px',
                borderBottom: '0.5px solid',
                borderColor: 'primary.tableBorder',
              }}
            >
              {formatVocabularyTestWord(word)}
            </Box>
            <Box
              sx={{
                fontSize: 17,
                color: 'text.secondary',
                p: '14px 16px',
                borderBottom: '0.5px solid',
                borderColor: 'primary.tableBorder',
              }}
            >
              {word.transcription || ''}
            </Box>
            <Box
              sx={{
                fontSize: 18,
                p: '14px 16px',
                borderBottom: '0.5px solid',
                borderColor: 'primary.tableBorder',
              }}
            >
              {word.translation || ''}
            </Box>
          </React.Fragment>
        ))}
      </Box>

      <Stack alignItems={'center'} spacing={1.5} sx={{ p: '16px 24px' }}>
        {totalPages > 1 && (
          <CompactPagination
            currentPage={safePage}
            onChange={setPage}
            totalPages={totalPages}
          />
        )}
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
          {filtered.length === 0
            ? '0–0 з 0 слів'
            : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(
                safePage * PAGE_SIZE,
                filtered.length,
              )} з ${fmt(filtered.length)} слів`}
        </Typography>
      </Stack>
    </Box>
  );
};

export default TestResultWordsTable;
